import React, { memo } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { UserMenuContent } from "./UserMenuContent";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface DesktopMenuProps {
  user: any;
  onSignOut: () => void;
  getInitials: (user: any) => string;
  unreadCount?: number;
  isScrolled?: boolean;
  /** Libellés courts pour la barre (ex. arabe sur tablette) */
  compactLabels?: boolean;
}

export const DesktopMenu = memo(
  ({
    user,
    onSignOut,
    getInitials,
    unreadCount = 0,
    isScrolled = false,
    compactLabels = false,
  }: DesktopMenuProps) => {
    const { t } = useTranslation();

    return (
      <>
        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="relative rounded-full h-10 w-10 p-0 border-2 border-emerald-soft hover:border-green-night/50 hover:bg-emerald-soft transition-all duration-300 group hover:scale-110 active:scale-95 shadow-sm hover:shadow-md"
              >
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-green-night/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <Avatar className="h-10 w-10 border-2 border-white shadow-md relative z-10">
                  <AvatarImage
                    src={user.user_metadata?.avatar_url}
                    alt={user.user_metadata?.first_name}
                    className="transition-transform duration-300 group-hover:scale-110"
                  />
                  <AvatarFallback className="bg-gradient-to-br from-green-night to-green-light text-white font-semibold text-sm">
                    {getInitials(user)}
                  </AvatarFallback>
                </Avatar>
                {/* Indicateur de statut en ligne - vert émeraude */}
                <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-night border-2 border-white rounded-full shadow-sm z-20" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              align="end" 
              className="w-72 p-2 bg-scandi-surface/95 backdrop-blur-xl border border-border/50 shadow-premium rounded-2xl"
            >
              <UserMenuContent
                user={user}
                onSignOut={onSignOut}
                getInitials={getInitials}
                unreadCount={unreadCount}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <div className="flex items-center gap-1.5 sm:gap-2.5 shrink-0">
            <Link to="/auth/register" className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  "h-9 rounded-xl border px-2.5 sm:px-4 text-xs sm:text-sm font-medium transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] active:scale-[0.99] whitespace-nowrap",
                  isScrolled
                    ? "border-border bg-muted/35 text-foreground hover:border-green-night/35 hover:bg-emerald-soft/45 hover:text-green-night"
                    : "border-white/35 bg-white/[0.06] text-white hover:border-white/52 hover:bg-white/[0.11] [text-shadow:0_1px_2px_rgba(0,0,0,0.18)]"
                )}
              >
                {compactLabels
                  ? t("common.signupShort", { defaultValue: t("common.signup") })
                  : t("common.signup")}
              </Button>
            </Link>
            <Link to="/auth/login" className="flex items-center">
              <Button
                variant="default"
                size="sm"
                className="h-9 rounded-xl bg-green-night px-3 sm:px-6 text-xs sm:text-sm font-semibold text-white shadow-sm shadow-green-night/20 transition-[background-color,box-shadow,transform] duration-300 ease-out hover:bg-green-light hover:shadow-md hover:shadow-green-night/25 active:scale-[0.99] whitespace-nowrap"
              >
                {compactLabels
                  ? t("common.connectionShort", { defaultValue: t("common.connection") })
                  : t("common.connection")}
              </Button>
            </Link>
          </div>
        )}
      </>
    );
  }
);

DesktopMenu.displayName = "DesktopMenu";
